/* iOS Safari zoom guard.
 *
 * Apple deliberately ignores `user-scalable=no` and `maximum-scale=1` in the
 * viewport meta on iPhone for accessibility reasons, so the only way to
 * actually stop pinch-zoom and double-tap-zoom inside a Yorm session is to
 * intercept the underlying gesture events in JavaScript.
 *
 * Listeners we register (only on iOS — Android Chrome honours the meta tag
 * correctly so we skip the gesture hijack there):
 *   - `gesturestart`  → fires when two fingers land. Cancelling this kills
 *                       pinch-zoom entirely on iOS Safari.
 *   - `dblclick`      → backup against the rare double-tap-zoom that some
 *                       iOS Safari versions still trigger despite touch-action.
 *   - `touchmove`     → only cancel multi-touch moves (event.touches.length > 1)
 *                       so single-finger scroll is untouched.
 *   - `wheel` ctrlKey → desktop Safari pinch on trackpad fires a wheel with
 *                       ctrlKey=true; blocking it stops the desktop zoom too.
 *
 * Returns a cleanup function so the effect can detach listeners on unmount.
 */
export function installZoomGuard() {
  if (typeof window === "undefined") return () => {};

  // We're aggressive on iOS where the meta tag is ignored; on Android the
  // viewport `maximum-scale=1` is honoured natively so we can stay light.
  const ua = window.navigator?.userAgent || "";
  const isiOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && "ontouchend" in document);

  const blockPinchGesture = (e) => {
    // iOS Safari emits its proprietary gesture* events when two fingers move
    // — cancelling at start prevents the entire zoom from beginning.
    e.preventDefault();
  };

  const blockMultiTouchMove = (e) => {
    if (e.touches && e.touches.length > 1) {
      e.preventDefault();
    }
  };

  const blockDoubleClickZoom = (e) => {
    // touch-action: manipulation kills this on most browsers, but older iOS
    // versions still produce a double-tap zoom that this catches.
    e.preventDefault();
  };

  const blockTrackpadPinch = (e) => {
    // Mac trackpad pinch in desktop Safari arrives as a wheel event with the
    // ctrl key flag flipped on. Blocking it stops the desktop "page zoom"
    // gesture without affecting normal scroll.
    if (e.ctrlKey) e.preventDefault();
  };

  // Some iOS versions need passive:false to allow preventDefault on touchmove.
  const opts = { passive: false };

  if (isiOS) {
    document.addEventListener("gesturestart", blockPinchGesture, opts);
    document.addEventListener("gesturechange", blockPinchGesture, opts);
    document.addEventListener("gestureend", blockPinchGesture, opts);
    document.addEventListener("touchmove", blockMultiTouchMove, opts);
    document.addEventListener("dblclick", blockDoubleClickZoom, opts);
  }

  // Desktop pinch (trackpad) on Safari / Chrome / Edge — applies everywhere.
  window.addEventListener("wheel", blockTrackpadPinch, opts);

  return () => {
    if (isiOS) {
      document.removeEventListener("gesturestart", blockPinchGesture, opts);
      document.removeEventListener("gesturechange", blockPinchGesture, opts);
      document.removeEventListener("gestureend", blockPinchGesture, opts);
      document.removeEventListener("touchmove", blockMultiTouchMove, opts);
      document.removeEventListener("dblclick", blockDoubleClickZoom, opts);
    }
    window.removeEventListener("wheel", blockTrackpadPinch, opts);
  };
}
